/* iter-72c — Extracted from EditorPanel: the giant toolbar row that
 * lives in the PanelFrame `actions={...}` slot.
 *
 * Presentational only. Every callback and boolean flag is threaded
 * from the parent so state ownership stays exactly where it was.
 * Preserves every original data-testid so tours and Playwright specs
 * keep passing.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 2
import {
  Search, Bookmark, History,
  ShieldCheck, BookOpenText, MessageSquare, GitBranch,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { HelpTip } from '@/components/HelpTip';
import AiAssistDropdown from './AiAssistDropdown';
import SaveStatusPill from './SaveStatusPill';
// WorkflowRibbon now rendered at AppShell level — import removed
import { cn } from '@/lib/utils';

export default function EditorHeaderActions({
  // Chapter navigation — searchable dropdown
  idx, chapters = [],
  onSelectChapter,
  // Legacy kept for backward-compat (not rendered)
  chaptersLength, onPrev, onNext,
  words = 0,
  // dialog / drawer toggles
  onOpenSearch, onOpenBookmarks, onToggleSuggestions, onToggleHistory,
  onTogglePlagiarism, onOpenBible,
  suggestionsOpen, historyOpen, plagiarismOpen,
  suggestionsBadge = 0,
  suggestionsLive = false,
  // save-state pill (iter55g17)
  saveState = 'idle', lastSavedAt = null, lastError = null, onSaveRetry, onSaveAs,
  // tone shift
  toneBusy, onToneShift,
  isDemo, canGenerate, enoughForTone, toneCost,
  // generate / continue
  busy, isStreaming,
  continueCost, draftCost, remaining, genTitle,
  enoughForContinue, enoughForDraft,
  onContinue, onDraft,
  // iter55g21 — Draft entire novel
  canBulkDraft = true, onDraftNovel, bulkDraftLockedTitle,
  // Feb-2026 · Generate Synopsis
  onGenerateSynopsis,
  // Plot Builder (Pro/Studio)
  onOpenPlotBuilder,
  // Feb-2026 · Simple Mode — hides the Plagiarism check button
  simpleMode,
  // iter-arc-walker — publishable score pill next to word count
  arcScore = null,
}) {
  const { t } = useTranslation();

  // Build a label for a chapter in the dropdown
  const chLabel = (ch, i) => {
    const num = ch?.number ?? ch?.chapter_number ?? (i + 1);
    const title = ch?.title || '';
    return title ? `Ch ${num} · ${title}` : `Chapter ${num}`;
  };

  return (
    <div className="flex flex-1 min-w-0 items-center gap-0.5 overflow-x-auto scroll-thin"
         style={{ scrollbarWidth: 'none' }}>

      {/* ── Chapter selector dropdown ── */}
      <Select
        value={String(idx)}
        onValueChange={(v) => onSelectChapter?.(parseInt(v, 10))}
      >
        <SelectTrigger
          className="h-6 min-w-[120px] max-w-[200px] border-0 bg-transparent px-1.5 text-[13px] font-medium text-foreground focus:ring-0 focus:ring-offset-0 hover:bg-secondary/50 rounded-sm"
          data-testid="editor-chapter-select"
        >
          <SelectValue>
            {chapters[idx] ? chLabel(chapters[idx], idx) : `Ch ${idx + 1}`}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72 overflow-y-auto">
          {chapters.map((ch, i) => (
            <SelectItem
              key={ch.id || i}
              value={String(i)}
              data-testid={`chapter-select-option-${i}`}
              className="text-[13px]"
            >
              {chLabel(ch, i)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* WorkflowRibbon now lives at AppShell level (above the workspace row) */}

      {/* iter55g17 — auto-save indicator */}
      <SaveStatusPill
        state={saveState}
        lastSavedAt={lastSavedAt}
        lastError={lastError}
        onRetry={onSaveRetry}
        onSaveAs={onSaveAs}
      />
      <span className="mx-1 h-4 w-px bg-panel-border" />

      <HelpTip
        slug="editor-search"
        title="Find in text"
        content={<>Opens a two-tab search: <b>this chapter</b> (client-side find with Prev/Next) and <b>whole project</b> (chapters, Story Bible, characters). Keyboard: <kbd className="rounded-sm border border-border bg-secondary px-1 font-mono-stat">Cmd/Ctrl + F</kbd>.</>}
      >
        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[13px]"
                onClick={onOpenSearch} data-testid="editor-search-btn"
                title={t('editor.searchTitle')}>
          <Search className="h-3.5 w-3.5" />
        </Button>
      </HelpTip>

      <HelpTip
        slug="editor-bookmarks"
        title="Bookmarks"
        content={<>Save your place by <b>page number</b> or by highlighting a passage. Toggle a bookmark to shared to let co-editors see it.</>}
      >
        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[13px]"
                onClick={onOpenBookmarks} data-testid="editor-bookmarks-btn"
                title={t('editor.bookmarksTitle')}>
          <Bookmark className="h-3.5 w-3.5" />
        </Button>
      </HelpTip>

      <HelpTip
        slug="suggestions-drawer"
        title="Suggestions & notes"
        content={<>Right-side drawer for editorial suggestions. Anyone can highlight text and <b>suggest an edit</b> or <b>add a note</b>. Anchored suggestions are highlighted in the manuscript body — amber for edits, sky-blue for notes. The project owner accepts or declines; accepted edits are applied automatically. The small dot next to the label shows the realtime channel status — green when co-editor edits will appear live, grey when reconnecting.</>}
      >
        <Button variant="ghost" size="sm" onClick={onToggleSuggestions}
                data-testid="editor-suggestions-btn" title={t('editor.notesTitle')}
                className={`h-6 gap-1 px-1.5 text-[13px] ${suggestionsOpen ? 'text-primary' : ''}`}>
          <MessageSquare className="h-3.5 w-3.5" />
          {t('editor.notes')}
          {/* iter-72e — realtime channel indicator (green=open, grey=reconnecting). */}
          <span
            data-testid="editor-suggestions-live-dot"
            data-live={suggestionsLive ? 'true' : 'false'}
            aria-label={suggestionsLive ? 'Realtime channel: open' : 'Realtime channel: reconnecting'}
            title={suggestionsLive ? 'Live — new notes appear instantly' : 'Reconnecting…'}
            className={
              'inline-block h-1.5 w-1.5 rounded-full '
              + (suggestionsLive
                ? 'bg-emerald-500 shadow-[0_0_6px_1px_rgba(16,185,129,0.6)]'
                : 'bg-muted-foreground/50')
            }
          />
          {suggestionsBadge > 0 && (
            <span
              data-testid="editor-suggestions-badge"
              className="ml-1 inline-flex min-w-[16px] items-center justify-center rounded-full bg-primary/15 px-1 text-[13px] font-semibold text-primary"
            >
              {suggestionsBadge}
            </span>
          )}
        </Button>
      </HelpTip>

      <Button variant="ghost" size="sm" onClick={onToggleHistory}
              data-testid="editor-history-btn" title={t('editor.historyTitle')}
              className={`h-6 gap-1 px-1.5 text-[13px] ${historyOpen ? 'text-primary' : ''}`}>
        <History className="h-3.5 w-3.5" /> {t('editor.history')}
      </Button>

      {!simpleMode && (
        <Button variant="ghost" size="sm" onClick={onTogglePlagiarism}
                data-testid="editor-plagiarism-btn" title={t('editor.plagiarismTitle')}
                className={`h-6 gap-1 px-1.5 text-[13px] ${plagiarismOpen ? 'text-primary' : ''}`}>
          <ShieldCheck className="h-3.5 w-3.5" /> {t('editor.plagiarism')}
        </Button>
      )}

      <Button variant="ghost" size="sm" onClick={onOpenBible}
              data-testid="editor-chapter-bible-btn"
              title={t('editor.chapterBibleTitle')}
              className="h-6 gap-1 px-1.5 text-[13px]">
        <BookOpenText className="h-3.5 w-3.5" /> {t('editor.chapterBible')}
      </Button>

      <span className="mx-1 h-4 w-px bg-panel-border" />

      {/* iter55g24 — consolidated AI Assist dropdown replaces the tone
       *  trio + Continue/Draft/Draft-novel row. Same testids inside. */}
      <AiAssistDropdown
        toneBusy={toneBusy}
        onToneShift={onToneShift}
        isDemo={isDemo}
        canGenerate={canGenerate}
        enoughForTone={enoughForTone}
        toneCost={toneCost}
        busy={busy}
        isStreaming={isStreaming}
        continueCost={continueCost}
        draftCost={draftCost}
        remaining={remaining}
        genTitle={genTitle}
        enoughForContinue={enoughForContinue}
        enoughForDraft={enoughForDraft}
        onContinue={onContinue}
        onDraft={onDraft}
        canBulkDraft={canBulkDraft}
        onDraftNovel={onDraftNovel}
        bulkDraftLockedTitle={bulkDraftLockedTitle}
        onGenerateSynopsis={onGenerateSynopsis}
        onOpenPlotBuilder={onOpenPlotBuilder}
      />
      {/* iter55g23 — Word count moved to the far right of the actions row
       *  so it never overlaps the prev/next chapter arrows on the left. */}
      {/* iter-arc-walker — Publishable score pill (hidden until first Arc Walker run) */}
      {arcScore !== null && (
        <span
          className={cn(
            'ml-auto flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[13px] font-semibold leading-none',
            arcScore >= 85
              ? 'bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-500/30'
              : arcScore >= 70
                ? 'bg-amber-500/15 text-amber-700 ring-1 ring-amber-500/30'
                : 'bg-rose-500/15 text-rose-700 ring-1 ring-rose-500/30',
          )}
          data-testid="editor-arc-score-pill"
          title={`Story Arc Walker publishable score: ${arcScore}/100${arcScore >= 85 ? ' — meets 85% threshold' : ' — below 85% threshold'}`}
        >
          <GitBranch className="h-2.5 w-2.5" />
          {arcScore}%
        </span>
      )}
      <span
        className={cn(
          'font-mono-stat text-[13px] text-muted-foreground',
          arcScore === null && 'ml-auto',
        )}
        data-testid="editor-word-count"
        title={t('editor.wordCountTitle')}
      >
        {t('editor.wordCount', { count: words })}
      </span>
    </div>
  );
}
